/**
 * Resolves an i18n key suffix ("One"/"Few"/"Many"/"Other") for a count-based
 * string, per the current UI language's plural rules.
 *
 * The shared t() helper (src/i18n/I18nContext.js) only does flat {var}
 * interpolation — it has no built-in CLDR pluralization. So instead of
 * passing a hand-computed English suffix (which produced grammatically wrong
 * Russian, e.g. "1 обсуждени" / "2 обсуждениs"), the caller picks the correct
 * pre-translated key per language and count using this helper, e.g.:
 *
 *   t(`teacher.dashboard.coursesLabel${pluralSuffix(lang, count)}`, { count })
 *
 * with `coursesLabelOne` / `coursesLabelFew` / `coursesLabelMany` / `coursesLabelOther`
 * keys defined in the ns module for each language that needs them.
 *
 * Dates are not formatted here or with a locale tag: utils/datetime.js writes them (Chromium has
 * no `uz` locale data, so a 'uz-UZ' tag printed "M10 17, Sat").
 */
export function pluralSuffix(lang, count) {
  const n = Math.abs(Number(count) || 0);

  if (lang === 'ru') {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return 'One';
    if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) return 'Few';
    return 'Many';
  }

  // Uzbek does not inflect nouns after a numeral — always the invariant form.
  if (lang === 'uz') return 'Other';

  // English (default): singular only for exactly 1.
  return n === 1 ? 'One' : 'Other';
}

export default pluralSuffix;
